import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  contentCards,
  IContentCardSlim,
  IContentCardsResult,
  isContentCardSlim,
} from "@origin-digital/event-dispatcher";
// personalisation-widget pins event-dispatcher@^7.59 and reporting-client@5.6.0,
// while the app uses 8.x and 6.x. We force a single major of each via the
// `resolutions` block in the root package.json so only one copy ships; the API
// surface the widget consumes (TOPICS, eventDispatcher, parseMarkdownEvent,
// configAnalytics, impression, interaction, interactionCard, logger, task) is
// preserved across these majors. widget-provider is also declared as a
// peerDependency by personalisation-widget/widget-helpers; yarn 1 does not
// auto-install peers, so it stays as a direct dep in package.json.
import { SlimCardWidget } from "@origin-digital/personalisation-widget";
import { Box, StackSection } from "@origin-digital/ods-core";
import { useFeatureFlag } from "@origin-digital/feature-flags";

const BRAZE_LOCATION = "helpSupport-landing";

const SlimCardWrapper = styled.div`
  [data-id="content-card-dismiss"] {
    background: none;
  }
  [data-id="content-card--slim"] {
    border-color: ${({ theme }) => theme.palette.borderColor.primary};
  }
`;

export const MarketingCard: React.FC = () => {
  const isMarketingCardEnabled = useFeatureFlag("marketingCard");
  const [cardContent, setCardContent] = useState<IContentCardSlim>();

  useEffect(() => {
    if (!isMarketingCardEnabled) return;

    let active = true;
    Promise.resolve()
      .then(() => contentCards({ location: BRAZE_LOCATION, limit: 1 }))
      .then((result: IContentCardsResult | undefined) => {
        if (!active || !result?.cards) return;
        const [card] = result.cards;
        if (card && isContentCardSlim(card)) {
          setCardContent(card);
        }
      })
      .catch(() => {
        // Additive only - silently ignore failures so the page is never blocked
      });

    return () => {
      active = false;
    };
  }, [isMarketingCardEnabled]);

  if (!cardContent) return null;

  // Conscious CLS tradeoff: the card mounts asynchronously after contentCards()
  // resolves, which pushes ContentSection below it down by the card's height.
  // We accept this shift rather than reserve space upfront because: (a) the prod
  // flag is off until Braze content is confirmed live, (b) reserving a fixed
  // min-height would create an empty gap whenever Braze returns no card, and
  // (c) the search bar above (the AC6 guarantee) is never affected.
  return (
    <StackSection
      paddingY="none"
      width="xlarge"
      data-id="marketing-card-section"
    >
      <Box marginBottom={{ xs: "medium", lg: "none" }}>
        <SlimCardWrapper>
          <SlimCardWidget {...cardContent} />
        </SlimCardWrapper>
      </Box>
    </StackSection>
  );
};
